import styled, { css } from 'styled-components'
import { mq } from 'styles/mq'
import { theme } from 'styles/theme'

export const Profile = styled.div`
  position: relative;
  display: grid;
  grid-template-columns: 1fr;
  grid-template-areas: 'title' 'photo' 'info' 'social' 'submit';
  border: 2px solid currentColor;

  ${mq.medium} {
    grid-template-columns: auto 1fr;
    grid-template-areas:
      'photo title'
      'photo info'
      'photo social'
      'photo submit';
  }

  ${mq.large} {
    grid-template-columns: auto 1fr 1fr;
    grid-template-areas:
      'photo title title'
      'photo info info'
      'photo social submit';
  }

  a {
    color: inherit;
  }
`

const gridCell = css`
  padding: 1.4rem;

  ${mq.medium} {
    padding: 1rem 2.5rem;
  }
`

export const PhotoContainer = styled.div`
  --photo-width: 100%;

  ${mq.medium} {
    --photo-width: 28rem;
  }

  ${mq.large} {
    --photo-width: 36rem;
  }

  ${gridCell}
  grid-area: photo;
  display: flex;
  align-items: flex-start;

  ${mq.smallOnly} {
    padding-bottom: 0;
  }

  ${mq.medium} {
    padding-block: 2.5rem;
  }

  img,
  svg {
    display: block;
    width: var(--photo-width);
  }

  ${mq.large} {
    img {
      align-self: stretch;
      object-fit: cover;
    }
  }
`

export const ImageWrapper = styled.div`
  align-self: stretch;
  position: relative;
  width: var(--photo-width);

  img {
    ${mq.medium} {
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
    }

    ${mq.large} {
      height: 100%;
    }
  }
`

export const Title = styled.h1`
  ${gridCell}
  grid-area: title;
  margin: 0;
  padding-bottom: 1.2rem;
  font-size: 2.1rem;
  line-height: 1;
  text-transform: uppercase;
  font-weight: inherit;
  text-transform: uppercase;
  border-left: 1px solid currentColor;
  border-bottom: 1px solid currentColor;

  ${mq.medium} {
    font-size: 3.5rem;
    padding-block: 2.5rem;
  }
`

export const Dl = styled.dl`
  display: flex;
  flex-wrap: wrap;
  align-self: center;
  grid-area: info;
  margin: 0;
`

export const Item = styled.div`
  ${gridCell}
  flex-basis: 100%;

  ${mq.medium} {
    border-left: 1px solid currentColor;
  }

  ${mq.large} {
    flex-basis: 50%;

    &:first-child,
    &:nth-child(2) {
      padding-top: 2.5rem;
    }

    &:last-child,
    &:nth-last-child(2) {
      padding-bottom: 2.5rem;
    }
  }
`

export const Dt = styled.dt`
  display: inline;
  font-size: 2.1rem;
  font-weight: bold;

  ${mq.smallOnly} {
    display: inline;
    font-size: 1.6rem;
    padding-right: 0.4em;
  }
`

export const Dd = styled.dd`
  display: inline;
  font-size: 2.1rem;
  margin: 0;
  font-style: italic;

  ${mq.smallOnly} {
    display: inline;
    font-size: 1.6rem;
  }

  ::first-letter {
    text-transform: uppercase;
  }
`

export const DdLarge = styled(Dd)`
  font: 700 3rem ${theme.fonts.headings};
`

export const Social = styled.div`
  ${gridCell}
  grid-area: social;
  display: flex;
  align-items: center;
  justify-content: space-between;
  border-top: 1px solid currentColor;

  ${mq.medium} {
    padding-block: 2.5rem;
    font-size: 1.8rem;
  }

  ul {
    list-style: none;
    padding: 0;
    margin: 0;
    margin-bottom: -0.5rem;
    flex-shrink: 0;
  }

  li {
    display: inline-block;

    a {
      display: block;
      color: var(--background);
      font-size: 3rem;
      width: 3rem;
      height: 3rem;
      margin-left: 0.8rem;
      transition: all 0.3s;
      background-color: var(--color);

      &:hover,
      &:focus {
        background-color: var(--color-primary);
      }
    }
  }

  ${mq.medium} {
    border-left: 1px solid currentColor;
  }
`

export const SubmitInfo = styled.div`
  ${gridCell}
  grid-area: submit;
  display: flex;
  align-items: center;
  border-top: 1px solid currentColor;
  line-height: 2.3rem;
  font-size: 1.6rem;

  ${mq.medium} {
    padding-block: 2.5rem;
    border-left: 1px solid currentColor;
    font-size: 1.8rem;
  }
`

export const Meta = styled.div`
  display: flex;

  ${mq.medium} {
    position: absolute;
    bottom: 100%;
    right: 0;
    transform: translateY(50%);
    justify-content: flex-end;
    z-index: 1;
  }

  > * {
    margin-right: 2rem;
  }

  ${mq.smallOnly} {
    > *:last-child {
      margin-right: 0;
    }
  }
`

export const UpdatedBadge = styled.div`
  padding: 0.5rem 1.2rem 0;
  height: 2.8rem;
  text-transform: uppercase;
  font-size: 1.6rem;
  background-color: var(--color-primary);
`

export const Label = styled.span`
  display: block;
  position: absolute;
  bottom: 100%;
  right: 50%;
  margin: 0.5rem 0;
  font-size: 1.6rem;
  white-space: nowrap;
  opacity: 0;
  transform: translateY(0.5rem);
  transition: all 0.3s;
  pointer-events: none;

  &::after {
    content: '';
    display: block;
    border-top: 1px solid currentColor;
    border-right: 1px solid currentColor;
    height: 1.3rem;
    transform: scaleY(0.7);
    transform-origin: 0 0;
    transition: transform 0.3s;
  }
`

export const VerifyStatus = styled.div<{ isConfirmed: boolean }>`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 2.8rem;
  height: 2.8rem;
  flex-shrink: 0;
  background-color: ${(props) => (props.isConfirmed ? '#00965e' : '#EE8735')};

  svg {
    margin: 0;
    font-size: 2rem;
  }

  &:hover ${Label} {
    opacity: 1;
    transform: translate3d(0, 0, 0);

    &::after {
      transform: none;
    }
  }
`

export const ProfileWrapper = styled.div`
  position: relative;
  margin: 5rem 0;
`

export const Source = styled.p`
  margin: 1.2rem 0 0;
  color: #a8a8a8;
  font-size: 1.6rem;
  font-style: italic;

  ${mq.medium} {
    margin-top: 1.8rem;
    font-size: 2.1rem;
  }
`
export const ArticleFeatured = styled.div`
  margin: auto;
  max-width: 103rem;

  iframe {
    display: block;
    max-width: 100%;
    margin: 3rem auto 4rem;
  }

  img {
    max-width: 100%;
    margin: auto;
    display: block;
  }
`
